import { useHead, useRoute, useStripTags } from '#imports'
import { computed, type Ref } from 'vue'
import type { ISeoData } from '~/types'

function replaceVariables(template: string, variables: Record<string, ISeoData>): string {
  return template.replace(/:([\w.]+):/g, (_, key) => {
    const value = variables[key]
    return typeof value !== 'undefined' ? String(value) : ''
  })
}

function flattenObject(obj: Record<string, ISeoData>, prefix = ''): Record<string, ISeoData> {
  return Object.keys(obj).reduce(
    (acc, key) => {
      const value = obj[key]
      const fullKey = prefix ? `${prefix}.${key}` : key

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        Object.assign(acc, flattenObject(value, fullKey))
      } else {
        acc[fullKey] = value
      }

      return acc
    },
    {} as Record<string, ISeoData>
  )
}

export function useSeoFromCustom(
  data: Ref<Record<string, ISeoData> | undefined>,
  baseUrl?: string,
  locale: string = 'ru'
) {
  const route = useRoute()
  const { t } = useI18n()

  // Use SEO data from app.vue to avoid duplicate requests
  // Access the global SEO state that's already fetched in app.vue
  const nuxtApp = useNuxtApp()
  const seoApiResponse = computed(() => {
    // Try to get SEO data from app's global state first
    if (nuxtApp._seoData) {
      return nuxtApp._seoData.value
    }

    // Fallback to useFetch if global state is not available
    const { data } = useFetch('/api/seo', {
      query: { path: route.path },
      key: `seo-custom-${route.path}`,
      default: () => null
    })
    return data.value
  })

  const matchedSeo = computed(() => seoApiResponse.value)

  const seoMeta = computed(() => {
    const entity = data.value || {}
    const variables = flattenObject(entity)
    const seoData = matchedSeo.value

    return {
      title: seoData?.meta_title
        ? replaceVariables(seoData.meta_title, variables)
        : entity.title || '',
      description: seoData?.meta_description
        ? replaceVariables(seoData.meta_description, variables)
        : useStripTags(entity.description) || '',
      keywords: seoData?.meta_keywords ? replaceVariables(seoData.meta_keywords, variables) : '',
      ogTitle: seoData?.og_title
        ? replaceVariables(seoData.og_title, variables)
        : entity.title || '',
      ogDescription: seoData?.og_description
        ? replaceVariables(seoData.og_description, variables)
        : useStripTags(entity.description) || '',
      twitterTitle: seoData?.og_title
        ? replaceVariables(seoData.og_title, variables)
        : entity.title || '',
      twitterDescription: seoData?.og_description
        ? replaceVariables(seoData.og_description, variables)
        : useStripTags(entity.description) || '',
      image: seoData?.og_image
        ? replaceVariables(seoData.og_image, variables)
        : (entity.image_url as string) || ''
    }
  })

  useHead({
    htmlAttrs: {
      lang: locale
    },
    title: () => `${seoMeta.value.title} ${t('seo.suffix.banks_kyrgyzstan')}`,
    meta: [
      { name: 'description', content: () => seoMeta.value.description },
      { name: 'keywords', content: () => seoMeta.value.keywords },
      { property: 'og:title', content: () => seoMeta.value.ogTitle },
      { property: 'og:description', content: () => seoMeta.value.ogDescription },
      { property: 'og:locale', content: locale === 'ky' ? 'ky_KG' : 'ru_RU' },
      { name: 'twitter:title', content: () => seoMeta.value.twitterTitle },
      { name: 'twitter:description', content: () => seoMeta.value.twitterDescription },
      seoMeta.value.image ? { property: 'og:image', content: () => seoMeta.value.image } : undefined,
      seoMeta.value.image ? { name: 'twitter:image', content: () => seoMeta.value.image } : undefined,
      baseUrl ? { property: 'og:url', content: baseUrl } : undefined
    ].filter(Boolean)
  })
}
